<script lang="ts">
  import { t } from '$core/i18n';
  import {
    socialStore,
    leaveGroup,
    deleteGroup
  } from '$features/social';
  import CreateGroupModal from '$ui/components/CreateGroupModal.svelte';
  import JoinGroupModal from '$ui/components/JoinGroupModal.svelte';



  let showCreate = $state(false);
  let showJoin = $state(false);
  let busy = $state(false);
  let confirmLeaveId = $state<string | null>(null);
  let confirmDeleteId = $state<string | null>(null);
  let copiedCode = $state<string | null>(null);

  $effect(() => {
    void socialStore.refreshAll();
  });

  function onKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (showCreate || showJoin) return;
    if (confirmLeaveId) {
      confirmLeaveId = null;
      return;
    }
    if (confirmDeleteId) {
      confirmDeleteId = null;
      return;
    }
    e.preventDefault();
  }

  async function onLeave(groupId: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await leaveGroup(groupId);
      await socialStore.refreshAll(true);
      confirmLeaveId = null;
    } finally {
      busy = false;
    }
  }

  async function onDelete(groupId: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await deleteGroup(groupId);
      await socialStore.refreshAll(true);
      confirmDeleteId = null;
    } finally {
      busy = false;
    }
  }

  async function copyCode(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      copiedCode = code;
      window.setTimeout(() => {
        if (copiedCode === code) copiedCode = null;
      }, 1500);
    } catch {
      // clipboard puede fallar en contextos no seguros; no es crítico
    }
  }

  async function onCreated(): Promise<void> {
    await socialStore.refreshAll(true);
  }
</script>

<svelte:window on:keydown={onKeydown} />

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('groups.title')}</h1>
    <div class="controls-right">
      <button class="primary" onclick={() => (showCreate = true)}>
        + {t('groups.create')}
      </button>
      <button class="secondary" onclick={() => (showJoin = true)}>
        {t('groups.join')}
      </button>
    </div>
  </header>

  {#if socialStore.loading && socialStore.groups.length === 0}
    <p class="empty">{t('groups.loading')}</p>
  {:else if socialStore.groups.length === 0}
    <p class="empty">{t('groups.noGroups')}</p>
  {:else}
    <ul class="list">
      {#each socialStore.groups as group (group.id)}
        <li class="row">
          <div class="row-info">
            <div class="row-texts">
              <span class="row-name">
                {group.name}
                {#if group.role === 'owner'}
                  <span class="badge">{t('groups.owner')}</span>
                {/if}
              </span>
              <span class="row-sub">
                {group.memberCount} {group.memberCount === 1 ? t('groups.member') : t('groups.members')}
              </span>
              <button
                class="code-btn"
                onclick={() => void copyCode(group.inviteCode)}
                title={t('groups.copyCode')}
              >
                {copiedCode === group.inviteCode ? t('groups.copied') : group.inviteCode}
              </button>
            </div>
          </div>

          {#if confirmLeaveId === group.id || confirmDeleteId === group.id}
            <div class="row-actions">
              <span class="warn">
                {confirmDeleteId === group.id
                  ? t('groups.confirmDelete')
                  : t('groups.confirmLeave')}
              </span>
              <button
                class="danger"
                disabled={busy}
                onclick={() =>
                  confirmDeleteId === group.id
                    ? void onDelete(group.id)
                    : void onLeave(group.id)}
              >
                {t('groups.confirmYes')}
              </button>
              <button
                class="secondary"
                disabled={busy}
                onclick={() => {
                  confirmLeaveId = null;
                  confirmDeleteId = null;
                }}
              >
                {t('groups.cancel')}
              </button>
            </div>
          {:else}
            <div class="row-actions">
              {#if group.role === 'owner'}
                <button
                  class="danger"
                  disabled={busy}
                  onclick={() => (confirmDeleteId = group.id)}
                >
                  {t('groups.delete')}
                </button>
              {:else}
                <button
                  class="danger"
                  disabled={busy}
                  onclick={() => (confirmLeaveId = group.id)}
                >
                  {t('groups.leave')}
                </button>
              {/if}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  {#if showCreate}
    <CreateGroupModal
      onClose={() => (showCreate = false)}
      onCreated={onCreated}
    />
  {/if}

  {#if showJoin}
    <JoinGroupModal
      onClose={() => (showJoin = false)}
      onJoined={onCreated}
    />
  {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    max-width: 900px;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .controls-right {
    display: flex;
    gap: var(--space-2);
  }

  .empty {
    padding: var(--space-8) var(--space-4);
    margin: 0;
    text-align: center;
    color: var(--color-text-muted);
    font-size: var(--text-body);
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    min-height: 64px;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .row-info {
    min-width: 0;
    flex: 1;
  }

  .row-texts {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .row-name {
    display: inline-flex;
    align-items: center;
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .row-sub {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .badge {
    margin-left: var(--space-2);
    padding: 0 var(--space-2);
    height: 18px;
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: var(--weight-medium);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-accent);
    border: var(--border-width) solid var(--color-accent);
    border-radius: var(--radius-xs);
  }

  .code-btn {
    align-self: flex-start;
    padding: 0;
    margin-top: var(--space-1);
    font-family: var(--font-mono);
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    background: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
  }

  .code-btn:hover {
    color: var(--color-text-primary);
  }

  .row-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex: 0 0 auto;
  }

  .warn {
    font-size: var(--text-sm);
    color: var(--color-danger);
  }

  .primary {
    height: 36px;
    padding: 0 var(--space-4);
    background: var(--color-accent);
    color: var(--color-accent-fg);
    border: var(--border-width) solid var(--color-accent);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out);
  }

  .primary:hover:not(:disabled) {
    background: var(--color-accent-hover);
    border-color: var(--color-accent-hover);
  }

  .secondary {
    height: 36px;
    padding: 0 var(--space-4);
    background: transparent;
    color: var(--color-text-secondary);
    border: var(--border-width) solid var(--color-border-strong);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
  }

  .secondary:hover:not(:disabled) {
    color: var(--color-text-primary);
    background: var(--color-surface-hover);
  }

  .danger {
    height: 36px;
    padding: 0 var(--space-4);
    background: transparent;
    color: var(--color-danger);
    border: var(--border-width) solid var(--color-danger);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
  }

  .danger:hover:not(:disabled) {
    color: var(--color-danger-fg);
    background: var(--color-danger);
  }

  .primary:disabled,
  .secondary:disabled,
  .danger:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .primary:focus-visible,
  .secondary:focus-visible,
  .danger:focus-visible,
  .code-btn:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }
</style>